import { type Accessor, createContext, createSignal, type ParentComponent, useContext } from "solid-js";

export interface TOCEntry {
  id: string;
  text: string;
  level: 1 | 2 | 3;
  messageId: string;
  blockId: string;
}

export interface TOCContextValue {
  entries: Accessor<TOCEntry[]>;
  registerHeading: (entry: TOCEntry) => void;
  unregisterHeading: (id: string) => void;
  currentSectionId: Accessor<string | undefined>;
  setCurrentSectionId: (id: string | undefined) => void;
}

const TOCContext = createContext<TOCContextValue>();

export const TOCProvider: ParentComponent = (props) => {
  const [entries, setEntries] = createSignal<TOCEntry[]>([]);
  const [currentSectionId, setCurrentSectionId] = createSignal<string | undefined>(undefined);

  const registerHeading = (entry: TOCEntry) => {
    setEntries((prev) => {
      const exists = prev.some((e) => e.id === entry.id);
      if (exists) {
        return prev;
      }
      return [...prev, entry];
    });
  };

  const unregisterHeading = (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const value: TOCContextValue = {
    entries,
    registerHeading,
    unregisterHeading,
    currentSectionId,
    setCurrentSectionId,
  };

  return <TOCContext.Provider value={value}>{props.children}</TOCContext.Provider>;
};

export const useTOCContext = (): TOCContextValue => {
  const context = useContext(TOCContext);
  if (!context) {
    throw new Error("useTOCContext must be used within a TOCProvider");
  }
  return context;
};

export const useTOCContextSafe = (): TOCContextValue | undefined => {
  return useContext(TOCContext);
};
